import { ChangeDetectionStrategy, Component, effect, inject, OnDestroy, OnInit } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { AbstractControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { TemplateManagerService } from '../../services/template-manager.service'
import { PermissionService } from '@core/auth/services/permission.service'
import { CERTIFICATE_CATEGORY_OPTIONS, TemplateForm } from '../../models/template.model'
import { SELECTABLE_COVERAGE_INSURANCE_TYPE_OPTIONS } from '@shared/models/coverage-insurance.model'
import { startWith } from 'rxjs'
import { ButtonDirective } from 'primeng/button'
import { AutoComplete, AutoCompleteCompleteEvent } from 'primeng/autocomplete'
import { IftaLabel } from 'primeng/iftalabel'
import { Select } from 'primeng/select'
import { ToggleSwitch } from 'primeng/toggleswitch'
import { Divider } from 'primeng/divider'
import { InputText } from 'primeng/inputtext'
import { Textarea } from 'primeng/textarea'
import { TemplateCuListComponent } from '@features/template/components/template-cu-list/template-cu-list.component'
import { UnsavedChangesGuard } from '@services/unsaved-changes.guard'

@Component({
  selector: 'albert-template-display',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    IftaLabel,
    Select,
    ToggleSwitch,
    Divider,
    InputText,
    Textarea,
    AutoComplete,
    ButtonDirective,
    TemplateCuListComponent,
  ],
  templateUrl: './template-display.component.html',
  styleUrl: './template-display.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TemplateDisplayComponent implements OnInit, OnDestroy {
  public readonly manager = inject(TemplateManagerService)
  public readonly permissionService = inject(PermissionService)
  protected readonly coverageTypeOptions = SELECTABLE_COVERAGE_INSURANCE_TYPE_OPTIONS
  protected readonly certificateCategoryOptions = CERTIFICATE_CATEGORY_OPTIONS
  protected readonly showDetails = toSignal(
    this.templateForm.controls.showDetails.valueChanges.pipe(
      startWith(this.templateForm.controls.showDetails.value)
    )
  )
  private readonly unsavedGuard = inject(UnsavedChangesGuard)

  constructor() {
    effect(() => {
      if (this.manager.isReadOnly()) {
        this.templateForm.disable({ emitEvent: false })
        this.templateForm.controls.showDetails.enable({ emitEvent: false })
      } else {
        this.templateForm.enable({ emitEvent: false })
      }
    })
  }

  public get shouldShowConditionError(): boolean {
    return (this.templateForm.errors?.['conditionExists'] === true) &&
      ((this.templateForm.get('certificateCategory')?.touched === true) ||
        (this.templateForm.get('coverageType')?.touched === true))
  }

  protected get templateForm(): FormGroup<TemplateForm> {
    return this.manager.templateForm
  }

  public ngOnInit(): void {
    this.unsavedGuard.register({
      condition: () => this.templateForm.dirty,
      confirmLeave: () => this.manager.confirmLeave()
    })
  }

  public ngOnDestroy(): void {
    this.unsavedGuard.unregister()
  }

  public onEdit(): void {
    this.manager.isReadOnly.set(false)
    this.manager.contentState.set('edit')
  }

  public onDuplicate(): void {
    this.manager.duplicate()
  }

  public onSave(): void {
    this.manager.save()
  }

  public onCancel(): void {
    this.manager.cancel()
  }

  public onClose(): void {
    this.manager.close()
  }

  public conditionFormElementValidity(ctrl: AbstractControl | null | undefined): boolean {
    return ((ctrl?.invalid === true) || (this.templateForm.errors?.['conditionExists'] === true)) && (ctrl?.touched ?? false)
  }

  public onDelete(): void {
    this.manager.delete()
  }

  protected onTagsSearch(event: AutoCompleteCompleteEvent): void {
    this.manager.searchTags(event.query)
  }
}
